import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import api from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { toast } from "sonner";
import { ShieldCheck, Lock, Gift } from "lucide-react";
import { inrFull } from "@/lib/format";

export default function Purchase() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [form, setForm] = useState({
    full_name: "", mobile: "", email: "", address: "", dob: "",
    family_members_count: 0, family_member_names: [],
    referral_code: (sp.get("ref") || "").trim().toUpperCase(),
  });

  useEffect(() => {
    api.get("/plans").then((r) => {
      const p = r.data.find((x) => x.plan_id === planId);
      setPlan(p);
    });
  }, [planId]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFamilyCount = (n) => {
    const names = Array.from({ length: n }, (_, i) => form.family_member_names[i] || "");
    setForm((f) => ({ ...f, family_members_count: n, family_member_names: names }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!plan) return;
    setLoading(true);
    try {
      const { data } = await api.post("/memberships/purchase", { plan_id: plan.plan_id, ...form });
      let paymentResult;
      if (!data.mock) {
        paymentResult = await openRazorpayCheckout({
          key: data.razorpay_key,
          amount_paise: data.amount_paise,
          order_id: data.order_id,
          name: "Nirmal Privilege Card",
          description: `${plan.name} membership`,
          prefill: { name: form.full_name, email: form.email, contact: form.mobile },
        });
      } else {
        await new Promise((r) => setTimeout(r, 900));
        paymentResult = {
          order_id: data.order_id,
          payment_id: `pay_mock_${Date.now()}`,
          signature: `sig_mock_${Date.now()}`,
        };
      }
      const verify = await api.post("/memberships/verify-payment", paymentResult);
      setFinalizing(true);
      if (verify.data.auto_account) {
        sessionStorage.setItem(`npc_creds_${verify.data.membership.member_id}`, JSON.stringify(verify.data.auto_account));
        if (verify.data.auto_account.token) {
          localStorage.setItem("vip_token", verify.data.auto_account.token);
        }
      }
      toast.success("Payment successful · Membership activated");
      await new Promise((r) => setTimeout(r, 1200));
      navigate(`/success/${verify.data.membership.member_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false); setFinalizing(false);
    }
  };

  if (!plan) return <SiteLayout><div className="p-10">Loading…</div></SiteLayout>;

  return (
    <SiteLayout>
      {finalizing && (
        <div className="fixed inset-0 z-[60] bg-[#0F2540]/90 backdrop-blur-sm grid place-items-center" data-testid="payment-finalizing-overlay">
          <div className="text-center px-6 max-w-md">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/10 border-4 border-white/30 border-t-transparent animate-spin" />
            <h2 className="mt-6 font-display text-2xl sm:text-3xl font-bold text-white">Creating your membership…</h2>
            <p className="mt-2 text-sm text-white/80">Please don't close this window. We're generating your digital card and setting up your dashboard.</p>
          </div>
        </div>
      )}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <span className="overline">Step 1 of 2 · Member details</span>
          <h1 className="mt-2 font-display text-4xl font-bold text-[#0F2540]">Activate your {plan.name} membership</h1>
          <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="purchase-form">
            <Field label="Full Name" value={form.full_name} onChange={(v) => setField("full_name", v)} required testid="p-full-name" />
            <Field label="Mobile Number" value={form.mobile} onChange={(v) => setField("mobile", v)} required testid="p-mobile" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setField("email", v)} required testid="p-email" />
            <Field label="Date of Birth" type="date" value={form.dob} onChange={(v) => setField("dob", v)} required testid="p-dob" />
            <div className="sm:col-span-2">
              <Field label="Address" value={form.address} onChange={(v) => setField("address", v)} required testid="p-address" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F2540]">Family Members ({plan.family_members} allowed)</label>
              <input
                type="number" min="0" max={Math.max(0, plan.family_members - 1)} value={form.family_members_count}
                onChange={(e) => handleFamilyCount(Math.min(plan.family_members - 1, Math.max(0, Number(e.target.value) || 0)))}
                className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
                data-testid="p-family-count"
              />
            </div>
            <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4">
              {form.family_member_names.map((n, i) => (
                <Field key={i} label={`Family member ${i + 1} name`} value={n} onChange={(v) => {
                  const arr = [...form.family_member_names]; arr[i] = v;
                  setForm((f) => ({ ...f, family_member_names: arr }));
                }} testid={`p-family-${i}`} />
              ))}
            </div>

            {/* Referral discount code — friends of an existing NPC member */}
            <div className="sm:col-span-2 rounded-2xl border border-[#E1E8F0] bg-[#FAFBFD] p-4 flex items-start gap-3">
              <Gift className="h-5 w-5 text-[#C89B3C] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <label className="text-sm font-semibold text-[#0F2540]">Referral discount code (optional)</label>
                <p className="text-xs text-[#5C6B7A] mt-0.5">Have a code from a friend? Enter it here — they earn membership-extension days when you join.</p>
                <input
                  type="text"
                  value={form.referral_code}
                  onChange={(e) => setField("referral_code", e.target.value.trim().toUpperCase())}
                  placeholder="NPC-XXXXXX"
                  data-testid="p-referral-code"
                  className="mt-2 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87] font-mono uppercase tracking-wide"
                />
              </div>
            </div>

            <div className="sm:col-span-2 mt-4 flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2 text-sm text-[#5C6B7A]"><Lock className="h-4 w-4" /> Secure · Razorpay (test mode)</div>
              <button disabled={loading} className="btn-primary" data-testid="continue-to-pay-btn">
                {loading ? "Processing…" : `Pay ${inrFull(plan.price)} · Continue`}
              </button>
            </div>
          </form>
        </div>
        <aside className="rounded-3xl bg-white border border-[#E1E8F0] p-8 h-max sticky top-24">
          <span className="overline">Order summary</span>
          <h3 className="mt-2 font-display text-2xl text-[#0F2540]">{plan.name} Plan</h3>
          <p className="text-sm text-[#5C6B7A] mt-1">{plan.tagline}</p>
          <div className="mt-6 flex items-baseline gap-1">
            <span className="font-display text-4xl font-bold text-[#0F2540]">{inrFull(plan.price)}</span>
            <span className="text-sm text-[#5C6B7A]">/{plan.duration_months}mo</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-[#0F2540]">
            {plan.benefits.map((b, i) => <li key={i} className="flex gap-2"><ShieldCheck className="h-4 w-4 text-[#1E4F87] mt-0.5" /><span>{b}</span></li>)}
          </ul>
        </aside>
      </section>
    </SiteLayout>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-[#0F2540]">{label}{required && " *"}</label>
      <input
        type={type} value={value} required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
        data-testid={testid}
      />
    </div>
  );
}
